/**
 * Simple helper classes which wraps the wltp helper functions.
 */
import Debug from "../../../../../common-deprecated/Debug";
import type { ConfigSpecsRequestObjectType } from "../../../../shared-logic/types/ConfigurationSpecificationsObjectType";
import type { CarType } from "../../../../shared-logic/types/CarType";
import type { BasketType } from "../../../../shared-logic/types/BasketType";
import type { ItemsType } from "../../../../../common-deprecated/redux/typeHelpers";
import { initializeTmefFunction } from "../../../../../common-deprecated/utils";
import type {
    CalculatePricesResultType,
    CalculateResultType,
    ExternalPriceCalulatedPriceType,
} from "../../../../shared-logic/types/externalPriceTypes";
import { formatPriceResponse, formatPricesResponse } from "../../../../shared-logic/utils/externalPriceUtils";
import type { CommonSettingsType } from "../../../../../common-deprecated/settings/fetchCommonSettings";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";

// Typed as "Record<string, unknown>" considering this function is only used to add some basic checks
const parseDebugInfo = (label: string, obj?: Record<string, unknown>): string => {
    if (!obj) {
        return `${label} not available yet`;
    }
    try {
        return JSON.stringify(obj);
    } catch (ex) {
        Debug.error(`Error parsing ${label}`, ex);
        return `Error parsing ${label} JSON`;
    }
};

class ExternalPriceCalculatorWrapper {
    // Using non-null assertion operator because we are sure that this will be set by the initialize function
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    calculate!: (
        config: ConfigSpecsRequestObjectType["specificationMapping"],
    ) => CalculateResultType | CalculatePricesResultType;

    private calculaterInput: ConfigSpecsRequestObjectType["specificationMapping"];

    private calculaterOutput?: CalculateResultType | CalculatePricesResultType;

    getDebugInfo(): { calculaterInput: string; calculaterOutput: string } {
        return {
            calculaterInput: parseDebugInfo("input", this.calculaterInput),
            calculaterOutput: parseDebugInfo("output", this.calculaterOutput),
        };
    }

    initialize(externalFunctions: string): boolean {
        const { success } = initializeTmefFunction(externalFunctions);

        if (!success) return false;

        // Cast window.calculate to the expected type
        if (typeof window.calculate === "function") {
            this.calculate = window.calculate as (
                config: ConfigSpecsRequestObjectType["specificationMapping"],
            ) => CalculateResultType | CalculatePricesResultType;
            return true;
        }

        return false;
    }

    calculatePrice(
        calcObj: ConfigSpecsRequestObjectType["specificationMapping"],
        car: CarType,
        basket: BasketType,
        items: ItemsType,
        commonSettings: CommonSettingsType,
    ): ExternalPriceCalulatedPriceType | null {
        if (!this.calculate) return null;

        // calculate results with supplied formula
        const calculatedResults = this.calculate(calcObj);

        // Save debug information
        this.calculaterInput = calcObj;
        this.calculaterOutput = calculatedResults;

        // There are 2 priceCalculationService endpoints. One containing 'price'. One containing 'prices'
        // Different inputs (calcObj) and different outputs (calculatedResults)

        // This is specific for 'price'
        if ((calculatedResults as CalculateResultType).result) {
            return formatPriceResponse(calculatedResults as CalculateResultType, car, basket, items);
            // This is specific for 'prices'
        } else {
            return formatPricesResponse(
                calculatedResults as CalculatePricesResultType,
                car.model.internalCode,
                car.price,
                commonSettings,
                AemFmComponent.Build,
            );
        }
    }
}

export const ExternalPriceCalculator = new ExternalPriceCalculatorWrapper();
